import React, { useState, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { usePOTemplateDnd } from './contextProvider/POTemplateDndContext';
import '../POTemplate.css';

const getControlLabels = (t) => ({
  TableTitle: t('PO_TEMPLATE.DETAIL_PAGE.CONTROL_LABELS.TABLE_TITLE'),
  TextArea: t('PO_TEMPLATE.DETAIL_PAGE.CONTROL_LABELS.TEXT_AREA'),
  Textbox: t('PO_TEMPLATE.DETAIL_PAGE.CONTROL_LABELS.TEXTBOX'),
  Dropdown: t('PO_TEMPLATE.DETAIL_PAGE.CONTROL_LABELS.DROPDOWN'),
  Checkbox: t('PO_TEMPLATE.DETAIL_PAGE.CONTROL_LABELS.CHECKBOX'),
  RadioButton: t('PO_TEMPLATE.DETAIL_PAGE.CONTROL_LABELS.RADIO_BUTTON'),
  DatePicker: t('PO_TEMPLATE.DETAIL_PAGE.CONTROL_LABELS.DATE_PICKER'),
});

const getDefaultPlaceholder = (controlType, label, t) => {
  const CONTROL_LABELS = getControlLabels(t);
  const baseLabel = label || CONTROL_LABELS[controlType] || controlType;

  switch (controlType) {
    case 'Textbox':
    case 'TextArea':
      return `${t('COMMON_LABEL.ENTER')} ${baseLabel}`;
    case 'Dropdown':
      return `${t('COMMON_LABEL.SELECT')} ${baseLabel}`;
    case 'Checkbox':
    case 'RadioButton':
      return `${t('COMMON_LABEL.SELECT')} ${baseLabel}`;
    case 'DatePicker':
      return `${t('COMMON_LABEL.SELECT')} ${baseLabel}`;
    default:
      return `${t('COMMON_LABEL.ENTER')} ${baseLabel}`;
  }
};

const getDefaultOptions = (controlType) => {
  if (['Checkbox', 'Dropdown', 'RadioButton'].includes(controlType)) {
    return [
      { id: Date.now(), name: 'Option 1' },
      { id: Date.now() + 1, name: 'Option 2' },
      { id: Date.now() + 2, name: 'Option 3' },
    ];
  }
  return [];
};

export default function ControlPropertiesModal({ isOpen, onClose, control, sectionId, columnIdx, controlIdx }) {
  const { t } = useTranslation('purchase-order');
  const { dispatch } = usePOTemplateDnd();
  const [formData, setFormData] = useState({
    label: '',
    placeholder: '',
    characterLimit: '',
    enable: true,
    required: false,
    isBold: false,
    options: [],
  });
  const [errors, setErrors] = useState({});

  const shouldShowOptions = () => {
    return ['Dropdown', 'Checkbox', 'RadioButton'].includes(control?.type);
  };

  useEffect(() => {
    if (control) {
      const CONTROL_LABELS = getControlLabels(t);
      const defaultOptions = control.options?.length > 0 ? control.options : getDefaultOptions(control.type);
      let defaultPlaceholder;
      if (control.label?.includes('(Copy)')) {
        defaultPlaceholder = getDefaultPlaceholder(control.type, control.label, t);
      } else {
        defaultPlaceholder = control.placeholder || getDefaultPlaceholder(control.type, control.label, t);
      }

      setFormData({
        label: control.label || CONTROL_LABELS[control.type] || '',
        placeholder: defaultPlaceholder,
        characterLimit: control.characterLimit || '',
        enable: control.enable !== false,
        required: control.required || false,
        isBold: control.isBold || false,
        options: ['Checkbox', 'Dropdown', 'RadioButton'].includes(control.type) ? defaultOptions : [],
      });
    }
  }, [control, t]);

  const validateOptions = (options) => {
    const errors = {};
    const names = options.map((option) => option.name?.trim().toLowerCase()).filter((name) => name);
    const duplicateNames = names.filter((name, index) => names.indexOf(name) !== index);

    if (duplicateNames.length > 0) {
      errors.options = t('COMMON_LABEL.DUPLICATE_OPTIONS_ERROR') || 'Options must be unique';
    }

    const emptyOptions = options.some((option) => !option.name?.trim());
    if (emptyOptions) {
      errors.emptyOptions = t('COMMON_LABEL.EMPTY_OPTIONS_ERROR') || 'All options must have a name';
    }

    return errors;
  };

  const hasErrors = () => {
    if (!shouldShowOptions()) return false;

    const optionErrors = validateOptions(formData?.options);
    return Object.keys(optionErrors).length > 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      if (field === 'label' && control) {
        const currentPlaceholder = prev.placeholder;
        const defaultPlaceholder = getDefaultPlaceholder(control.type, prev.label, t);

        if (!currentPlaceholder || currentPlaceholder === defaultPlaceholder) {
          newData.placeholder = getDefaultPlaceholder(control.type, value, t);
        }
      }

      return newData;
    });

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleAddOption = () => {
    const newOption = {
      id: Date.now(),
      name: '',
      value: '',
    };
    setFormData((prev) => ({
      ...prev,
      options: [...prev.options, newOption],
    }));
  };

  const handleRemoveOption = (index) => {
    if (formData.options.length > 1) {
      setFormData((prev) => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index),
      }));
    }
  };

  const handleOptionChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.map((option, i) => (i === index ? { ...option, [field]: value } : option)),
    }));

    if (errors.options || errors.emptyOptions) {
      setErrors((prev) => ({
        ...prev,
        options: null,
        emptyOptions: null,
      }));
    }
  };

  const handleSave = () => {
    if (shouldShowOptions()) {
      const optionErrors = validateOptions(formData.options);
      if (Object.keys(optionErrors).length > 0) {
        setErrors(optionErrors);
        return;
      }
    }

    const newProps = {
      label: formData.label,
      placeholder: formData.placeholder,
      enable: formData.enable,
      required: formData.required,
      isBold: formData.isBold,
      options: formData?.options || [],
    };

    if (formData.characterLimit !== '' && formData.characterLimit !== null && formData.characterLimit !== undefined) {
      const parsedLimit = parseInt(formData.characterLimit);
      if (!isNaN(parsedLimit) && parsedLimit >= 0) {
        newProps.characterLimit = parsedLimit;
      }
    } else if (formData.characterLimit === '') {
      newProps.characterLimit = 0;
    }

    dispatch({
      type: 'EDIT_CONTROL',
      sectionId,
      columnIdx,
      controlIdx,
      newProps,
    });
    onClose();
  };

  const handleClose = () => {
    if (control) {
      const CONTROL_LABELS = getControlLabels(t);
      const defaultOptions = control.options?.length > 0 ? control.options : getDefaultOptions(control.type);
      let defaultPlaceholder;
      if (control.label?.includes('(Copy)')) {
        defaultPlaceholder = getDefaultPlaceholder(control.type, control.label, t);
      } else {
        defaultPlaceholder = control.placeholder || getDefaultPlaceholder(control.type, control.label, t);
      }

      setFormData({
        label: control.label || CONTROL_LABELS[control.type] || '',
        placeholder: defaultPlaceholder,
        characterLimit: control.characterLimit || '',
        enable: control.enable !== false,
        required: control.required || false,
        isBold: control.isBold || false,
        options: ['Checkbox', 'Dropdown', 'RadioButton'].includes(control.type) ? defaultOptions : [],
      });
    }
    setErrors({});
    onClose();
  };

  if (!isOpen || !control) return null;

  const CONTROL_LABELS = getControlLabels(t);

  return (
    <div className="common-config-container z-index-99">
      <Card className="common-custom-card">
        <Card.Header className="d-flex">
          <p className="mb-0 f-24 flex-fill text-center">
            {control?.type || CONTROL_LABELS[control?.type]} {t('PO_TEMPLATE.DETAIL_PAGE.PROPERTIES')}
          </p>
          <button name="close" aria-label="close" onClick={handleClose} className="btn ml-auto" type="button">
            <span className="icon-close cursor-pointer" />
          </button>
        </Card.Header>

        <Card.Body className="overflow-auto p-0 f-14">
          <div className="p-24">
            <strong className="d-block text-bold">{control?.label || CONTROL_LABELS[control?.type]}</strong>
            <div className="bold-label-div mb-8">
              <strong className="d-block text-bold">{t('COMMON_LABEL.BOLD_LABEL')}</strong>
              <div className=" px-2 mt-1">
                <div className="bold-check-control">
                  <input
                    type="checkbox"
                    id="boldLabel"
                    onChange={(e) => handleInputChange('isBold', e.target.checked)}
                    checked={formData?.isBold}
                    name="boldLabel"
                  />
                  <label htmlFor="boldLabel" title={t('CUSTOM_MODULES.LIBRARY.CONTROL_CONFIG.CONFIG_MODAL.BOLD')}>
                    <span className="icon-bold_new" />
                  </label>
                </div>
              </div>
            </div>
            <div className="common-input-control no-label mb-4">
              <div className="d-flex">
                <input
                  type="text"
                  className="form-control "
                  placeholder={`${t('COMMON_LABEL.ENTER')} ${t('COMMON_LABEL.LABEL')}`}
                  value={formData.label || ''}
                  onChange={(e) => handleInputChange('label', e.target.value)}
                  maxLength={50}
                />
                <div className="text-count ml-2">{(formData.label || '').length}/50</div>
              </div>
            </div>
            {['Textbox', 'TextArea', 'Dropdown', 'DatePicker'].includes(control?.type) && (
              <div className="common-input-control no-label mb-4">
                <div className="d-flex">
                  <input
                    type="text"
                    className="form-control "
                    placeholder={`${t('COMMON_LABEL.ENTER')} ${t('COMMON_LABEL.PLACEHOLDER')}`}
                    value={formData.placeholder || ''}
                    onChange={(e) => handleInputChange('placeholder', e.target.value)}
                    maxLength={100}
                  />
                  <div className="text-count ml-2">{(formData.placeholder || '').length}/100</div>
                </div>
              </div>
            )}
            {control?.type === 'Textbox' && (
              <div className="common-input-control no-label mb-4">
                <input
                  type="number"
                  className="form-control "
                  placeholder={`${t('COMMON_LABEL.ENTER')} ${t('COMMON_LABEL.CHARACTER_LIMIT')}`}
                  value={formData.characterLimit || ''}
                  onChange={(e) => handleInputChange('characterLimit', e.target.value)}
                  min="1"
                />
              </div>
            )}
            {shouldShowOptions() && (
              <div className="p-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <strong>{t('COMMON_LABEL.OPTIONS') || 'Options'}</strong>
                </div>
                {formData.options.map((option, index) => {
                  return (
                    <div key={option.id || index} className="border rounded d-flex align-items-center mb-2">
                      <div className="flex-fill common-input-control no-label m-0">
                        <input
                          type="text"
                          className="form-control "
                          placeholder={t('COMMON_LABEL.OPTION_NAME') || 'Option Name'}
                          value={option.name || ''}
                          onChange={(e) => handleOptionChange(index, 'name', e.target.value)}
                          maxLength={50}
                        />
                      </div>
                      {index === formData.options.length - 1 && (
                        <button type="button" aria-label="Add Option" className="btn" onClick={handleAddOption}>
                          <i className="icon-Add" />
                        </button>
                      )}
                      <button
                        type="button"
                        aria-label="Remove Option"
                        className="btn px-2"
                        onClick={() => handleRemoveOption(index)}
                        disabled={formData.options.length <= 1}
                      >
                        <i className={`icon-Delete f-20 ${formData.options.length <= 1 ? 'text-muted' : ''}`} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="common-checkbox bold my-2">
              <input type="checkbox" id="enable_0" name="Enable" checked={formData?.enable} onChange={(e) => handleInputChange('enable', e.target.checked)} />
              <label htmlFor="enable_0" className="chklabel">
                <span>{t('PO_TEMPLATES.EDIT_REQUIRED_ENABLE.ENABLE')}</span>
              </label>
            </div>
            <div className="common-checkbox bold my-2">
              <input
                type="checkbox"
                id="require_1"
                name="Required"
                checked={formData?.required}
                onChange={(e) => handleInputChange('required', e.target.checked)}
              />
              <label htmlFor="require_1" className="chklabel">
                <span>{t('PO_TEMPLATES.EDIT_REQUIRED_ENABLE.REQUIRED')}</span>
              </label>
            </div>
          </div>
        </Card.Body>

        <Card.Footer>
          <button
            aria-label={t('CUSTOM_MODULE.SAVE')}
            className={`btn d-inline-flex p-0 ${hasErrors() ? 'disabled' : ''}`}
            type="button"
            onClick={handleSave}
            disabled={hasErrors()}
            style={{ opacity: hasErrors() ? 0.6 : 1 }}
          >
            <span className="icon-Tick-1 f-24" />
          </button>
        </Card.Footer>
      </Card>
    </div>
  );
}
